/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Collapse from '@material-ui/core/Collapse';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';

import CentralForm from 'components/CentralForm/CentralForm';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { ENV } from 'config';

import styles from './Error.module.scss';

export type AppInsightsInfo = {
  sessionId?: string,
  operationId?: String,
};

export type ErrorPageProps = {
  code?: number | string,
  stacktrace?: string,
  message?: string,
  appInsightsInfo?: AppInsightsInfo,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class ErrorPage extends ComponentWithInjectedProps<
  ErrorPageProps,
  {},
  InjectedProps
> {
  @observable showingStackTrace = false;

  getErrorTitle(code?: string | number) {
    const { t } = this.props;

    switch (String(code)) {
      case '401':
        return t('general:401');
      case '404':
        return t('general:404');
      case '500':
        return t('general:500');
      default:
        return 'Unknown Error';
    }
  }

  handleExpandClick = () => {
    this.showingStackTrace = !this.showingStackTrace;
  };

  handleCopyButtonClick = () => {
    navigator.clipboard.writeText(this.props.stacktrace);
  };

  renderStackTrace = () => {
    if (ENV !== 'production' && ENV !== 'staging') {
      const { stacktrace, t } = this.props;

      if (!stacktrace) {
        return (
          <div className={styles.stackArea}>
            <span>{t('general:no_stack_trace')}</span>
          </div>
        );
      }

      return (
        <div className={styles.stackArea}>
          <Card>
            <CardHeader
              className={styles.cardHeader}
              title="Stack Trace"
              action={
                <IconButton onClick={this.handleExpandClick} style={{ marginRight: 10 }}>
                  <svg className={styles.expandButtonIcon}>
                    <use
                      xlinkHref={this.showingStackTrace ? '#icon-chevron-down' : '#icon-chevron-up'}
                    />
                  </svg>
                </IconButton>
              }
            />
            <Collapse in={this.showingStackTrace}>
              <CardContent>
                <div className={styles.stackTrace}>
                  <code>
                    <pre>{stacktrace}</pre>
                  </code>
                </div>
              </CardContent>
              <CardActions>
                <Button onClick={this.handleCopyButtonClick} variant="contained" color="primary">
                  Copy
                </Button>
              </CardActions>
            </Collapse>
          </Card>
        </div>
      );
    }
  };

  renderAppInsightsInfo = () => {
    const { appInsightsInfo } = this.props;

    const hasAppInsightsInfo = !!(
      appInsightsInfo &&
      (appInsightsInfo.operationId || appInsightsInfo.sessionId)
    );

    if (hasAppInsightsInfo) {
      return (
        <div className={styles.appInsightsInfoWrapper}>
          {appInsightsInfo.sessionId && (
            <p>
              <b>Session ID:</b> <code>{appInsightsInfo.sessionId}</code>
            </p>
          )}
          {appInsightsInfo.operationId && (
            <p>
              <b>Operation ID:</b> <code>{appInsightsInfo.operationId}</code>
            </p>
          )}
        </div>
      );
    }
  };

  render() {
    const { t, message, code } = this.props;

    return (
      <CentralForm wide={this.showingStackTrace}>
        <div className={styles['error-page']}>
          <h1>{this.getErrorTitle(code)}</h1>
          <p>{message}</p>
        </div>

        {this.renderStackTrace()}
        {this.renderAppInsightsInfo()}
      </CentralForm>
    );
  }
}
